import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface Payment {
  id: number;
  job_id: number;
  payer_id: number;
  payee_id: number;
  amount: number;
  status: string;
  stripe_payment_intent_id?: string;
  payment_method?: string;
  description?: string;
  created_at: string;
  updated_at: string;
  payer_name?: string;
  payee_name?: string;
  job_title?: string;
}

export interface PaymentsState {
  payments: Payment[];
  loading: boolean;
  error: string | null;
}

const initialState: PaymentsState = {
  payments: [],
  loading: false,
  error: null,
};

export const fetchPayments = createAsyncThunk(
  'payments/fetchPayments',
  async (
    filters: { status?: string; limit?: number; offset?: number; startDate?: string; endDate?: string },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });

      const response = await api.get(`/payments?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch payments');
      }
      return rejectWithValue('Failed to fetch payments');
    }
  }
);

export const createPaymentIntent = createAsyncThunk(
  'payments/createPaymentIntent',
  async (
    data: {
      amount: number;
      currency?: string;
      description?: string;
      jobId: number;
      payeeId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/payments/intent', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to create payment intent');
      }
      return rejectWithValue('Failed to create payment intent');
    }
  }
);

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createPaymentIntent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaymentIntent.fulfilled, (state, action) => {
        state.loading = false;
        state.payments.unshift(action.payload);
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default paymentsSlice.reducer;
